/**
 * ============================================
 * ANALYTICS COUNTRY FILTER
 * ============================================
 *
 * The worker ships a separate country payload (GET /api/v1/analytics/countries):
 * every additive counter partitioned by the country ON THE EVENT, plus the
 * player-set cards (onboarding, level-editor onboarding, churn) split by each
 * player's country. For a country selection we sum the selected partitions
 * and splice them into a copy of the snapshot under the SAME field names, so
 * deriveRangeData (date range) and every card work unchanged.
 *
 * Semantics under a country filter:
 *  - additive cards follow the country AND the date range;
 *  - level completion / quit / restart become attempt-based (as with a range);
 *  - onboarding, level-editor onboarding and churn stay all-time (player sets).
 */

import type {
  AnalyticsData, ChurnPoint, CinematicStats, DailySession, OnboardingStep, TopEditorBlocks,
} from '@/hooks/useAnalytics';
import { countryName, type CountryOption } from '@/utils/countryRegions';

type Num = Record<string, number>;
type Accum = { sum: number; count: number };

/** One country's partition (field names match the main snapshot where one exists). */
export interface CountrySlice {
  globalTimeAccumByDay?: Record<string, Record<string, Accum>>;
  levelTimesByDay?: Record<string, Record<string, Accum>>;
  editorGrossSecondsByDay?: Record<string, Num>;
  levelSaveCountsByDay?: Record<string, Num>;
  levelItemsCollectedByDay?: Record<string, Record<string, Num>>;
  mainMenuButtonCountsByDay?: Record<string, Num>;
  multiplierPickedCountsByDay?: Record<string, Record<string, Num>>;
  characterCountsByDay?: Record<string, Num>;
  levelEntryCountsByDay?: Record<string, Num>;
  levelCompletionCountsByDay?: Record<string, Num>;
  levelRestartCountsByDay?: Record<string, Num>;
  dailyEditorOpens?: Record<string, Num>;
  dailySessionDurations?: Record<string, Accum>;
  dailyFriendRequestSent?: Num;
  dailyFriendRequestInteraction?: Record<string, Num>;
  dailyFriendUnfriended?: Num;
  eventCounts?: Num;
  block3dCounts?: Num;
  block2dCounts?: Num;
  cinematicStats?: Record<string, { skipped?: number; completed?: number }>;
  miscCounts?: Num;
}

export interface AnalyticsCountryPayload {
  available?: boolean;
  version: number;
  /** false until a full resync has built the per-country history */
  complete: boolean;
  generatedAt: string;
  countries: Record<string, { name: string; players: number }>;
  byCountry: Record<string, CountrySlice>;
  onboarding: Record<string, Num>;
  leOnboarding: Record<string, [number, number]>;
  churn: Record<string, Num>;
}

/** Deep-add numeric leaves of `src` into `dst` (shapes are identical across countries). */
function addInto(dst: any, src: any): any {
  for (const [k, v] of Object.entries(src || {})) {
    if (v && typeof v === 'object') {
      dst[k] = addInto(dst[k] && typeof dst[k] === 'object' ? dst[k] : {}, v);
    } else {
      dst[k] = (Number(dst[k]) || 0) + (Number(v) || 0);
    }
  }
  return dst;
}

const r1 = (x: number) => Math.round(x * 10) / 10;

function blockStats(counts: Num | undefined): TopEditorBlocks {
  const entries = Object.entries(counts || {});
  const total = entries.reduce((s, [, n]) => s + n, 0);
  if (!total) return { blocks: [] };
  return {
    blocks: entries.sort((a, b) => b[1] - a[1])
      .map(([block, count]) => ({ block, count, percentage: r1((count / total) * 100) })),
    totalPlacements: total,
  };
}

/** Mirrors analytics_processor._finalize_onboarding_funnel. */
function onboardingFunnel(counts: Num): OnboardingStep[] {
  const entries = Object.entries(counts);
  if (!entries.length) return [];
  let start = counts['start'] || 0;
  if (!start) start = Math.max(...entries.map(([, n]) => n));
  let startEntry: OnboardingStep | null = null;
  let exploreEntry: OnboardingStep | null = null;
  const others: OnboardingStep[] = [];
  for (const [checkpoint, players] of entries) {
    const e = { checkpoint, players, percentage: r1(Math.min(start ? (players / start) * 100 : 0, 100)) };
    if (checkpoint.toLowerCase() === 'start') startEntry = e;
    else if (checkpoint.toLowerCase() === 'explore') exploreEntry = e;
    else others.push(e);
  }
  others.sort((a, b) => b.players - a.players);
  return [...(startEntry ? [startEntry] : []), ...others, ...(exploreEntry ? [exploreEntry] : [])];
}

function cinematicStats(raw: CountrySlice['cinematicStats']): CinematicStats {
  const rows = Object.entries(raw || {}).map(([id, s]) => {
    const skipped = s.skipped || 0;
    const completed = s.completed || 0;
    return { id, skipped, completed, skipRate: skipped + completed ? r1((skipped / (skipped + completed)) * 100) : 0 };
  });
  const totalSkipped = rows.reduce((a, r) => a + r.skipped, 0);
  const totalCompleted = rows.reduce((a, r) => a + r.completed, 0);
  const total = totalSkipped + totalCompleted;
  return { skipRate: total ? r1((totalSkipped / total) * 100) : 0, totalSkipped, totalCompleted, totalViews: total, byCinematic: rows };
}

function dailySessions(durations: Record<string, Accum> | undefined): { avg: number; daily: DailySession[] } {
  const days = Object.keys(durations || {}).sort();
  let sum = 0;
  let count = 0;
  const daily = days.map(d => {
    const a = durations![d];
    sum += a.sum;
    count += a.count;
    const dt = new Date(`${d}T00:00:00`);
    return {
      date: dt.toLocaleDateString('en-US', { month: 'short', day: '2-digit' }),
      fullDate: d,
      avgSession: a.count ? r1(a.sum / a.count / 60) : 0,
      sessionCount: a.count,
    };
  });
  return { avg: count ? r1(sum / count / 60) : 0, daily };
}

/** Dropdown options: every country the payload knows, with all-time players. */
export function countryOptions(payload: AnalyticsCountryPayload | null, data: AnalyticsData | null): CountryOption[] {
  if (payload) {
    return Object.entries(payload.countries).map(([code, c]) => ({
      code, name: c.name && c.name !== code ? c.name : countryName(code), count: c.players,
    }));
  }
  return (data?.geo || []).map(g => ({ code: g.country_code, name: g.country || countryName(g.country_code), count: g.players }));
}

/**
 * The snapshot as if only the selected countries existed. Returns `data`
 * unchanged for "All" (empty selection).
 */
export function applyCountryFilter(
  data: AnalyticsData,
  payload: AnalyticsCountryPayload,
  selected: string[],
): AnalyticsData {
  if (!selected.length) return data;
  const m: CountrySlice = {};
  const onboarding: Num = {};
  const churn: Num = {};
  let leStarted = 0;
  let leEnded = 0;
  let players = 0;
  for (const cc of selected) {
    addInto(m, payload.byCountry[cc]);
    addInto(onboarding, payload.onboarding[cc]);
    addInto(churn, payload.churn[cc]);
    const le = payload.leOnboarding[cc];
    if (le) { leStarted += le[0]; leEnded += le[1]; }
    players += payload.countries[cc]?.players || 0;
  }

  const misc = m.miscCounts || {};
  const reg = misc.contest_registered || 0;
  const skip = misc.contest_skipped || 0;
  const fbInit = misc.feedback_initiated || 0;
  const fbSub = misc.feedback_submitted || 0;
  const sessions = dailySessions(m.dailySessionDurations);
  const eventTotal = Object.values(m.eventCounts || {}).reduce((a, b) => a + b, 0);
  const churnPoints: ChurnPoint[] = Object.entries(churn)
    .map(([category, count]) => ({ category, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 15);
  const sel = new Set(selected);
  const geo = (data.geo || []).filter(g => sel.has(g.country_code))
    .map(g => ({ ...g, percentage: players ? r1((g.players / players) * 100) : 0 }));

  return {
    ...data,
    totalPlayers: players,
    regions: selected.length,
    geo,
    avgSessionLength: sessions.avg,
    dailySessions: sessions.daily,
    timeSeriesData: {} as AnalyticsData['timeSeriesData'],
    onboardingFunnel: onboardingFunnel(onboarding),
    churnPoints,
    cinematicStats: cinematicStats(m.cinematicStats),
    feedbackStats: { initiated: fbInit, submitted: fbSub, submissionRate: fbInit ? r1((fbSub / fbInit) * 100) : 0 },
    levelEditorOnboarding: {
      started: leStarted, completed: leEnded, completionRate: leStarted ? r1((leEnded / leStarted) * 100) : 0,
    },
    eventCounts: { total: eventTotal, byType: m.eventCounts || {} },
    topEditorBlocks: blockStats(m.block3dCounts),
    topEditor2dBlocks: blockStats(m.block2dCounts),
    contestStats: { totalRegistered: reg, totalSkipped: skip, total: reg + skip, skipRate: reg + skip ? r1((skip / (reg + skip)) * 100) : 0 },
    dailyEditorOpens: m.dailyEditorOpens || {},
    dailyFriendRequestSent: m.dailyFriendRequestSent || {},
    dailyFriendRequestInteraction: m.dailyFriendRequestInteraction || {},
    dailyFriendUnfriended: m.dailyFriendUnfriended || {},
    globalTimeAccumByDay: m.globalTimeAccumByDay || {},
    levelTimesByDay: m.levelTimesByDay || {},
    editorGrossSecondsByDay: m.editorGrossSecondsByDay || {},
    levelSaveCountsByDay: m.levelSaveCountsByDay || {},
    levelItemsCollectedByDay: m.levelItemsCollectedByDay || {},
    mainMenuButtonCountsByDay: m.mainMenuButtonCountsByDay || {},
    multiplierPickedCountsByDay: m.multiplierPickedCountsByDay || {},
    characterCountsByDay: m.characterCountsByDay || {},
    levelEntryCountsByDay: m.levelEntryCountsByDay || {},
    levelCompletionCountsByDay: m.levelCompletionCountsByDay || {},
    levelRestartCountsByDay: m.levelRestartCountsByDay || {},
  };
}

// ---- Editor Gross Statistics: top countries per editor ----------------------

/** editor key in the rollups -> save/publish suffix in levelSaveCountsByDay */
export const EDITOR_SAVE_SUFFIX: Record<string, string | null> = {
  level_editor: '3d', editor_2d: '2d', track_editor: 'track', coop_level_editor: 'coop',
  avatar_editor: null, npc_editor: null, chassis_editor: null,
};

export interface EditorCountryRow {
  code: string;
  name: string;
  seconds: number;
  opens: number;
  saves: number;
  publishes: number;
}

/**
 * Per editor, the top `n` countries by total time in the date range, among the
 * selected countries (all when the selection is empty).
 */
export function editorTopCountries(
  payload: AnalyticsCountryPayload,
  selected: string[],
  inRange: (d: string) => boolean,
  n = 5,
): Record<string, EditorCountryRow[]> {
  const codes = selected.length ? selected : Object.keys(payload.byCountry);
  const perEditor: Record<string, EditorCountryRow[]> = {};
  for (const editor of Object.keys(EDITOR_SAVE_SUFFIX)) perEditor[editor] = [];
  for (const cc of codes) {
    const s = payload.byCountry[cc];
    if (!s) continue;
    const secs: Num = {};
    const opens: Num = {};
    const saves: Num = {};
    for (const [d, m] of Object.entries(s.editorGrossSecondsByDay || {})) if (inRange(d)) addInto(secs, m);
    for (const [d, m] of Object.entries(s.dailyEditorOpens || {})) if (inRange(d)) addInto(opens, m);
    for (const [d, m] of Object.entries(s.levelSaveCountsByDay || {})) if (inRange(d)) addInto(saves, m);
    const name = payload.countries[cc]?.name || countryName(cc);
    for (const [editor, suffix] of Object.entries(EDITOR_SAVE_SUFFIX)) {
      const seconds = secs[editor] || 0;
      const o = opens[editor] || 0;
      if (!seconds && !o) continue;
      perEditor[editor].push({
        code: cc, name, seconds, opens: o,
        saves: suffix ? saves[`uploaded_${suffix}`] || 0 : 0,
        publishes: suffix ? saves[`published_${suffix}`] || 0 : 0,
      });
    }
  }
  for (const editor of Object.keys(perEditor)) {
    perEditor[editor] = perEditor[editor].sort((a, b) => b.seconds - a.seconds || b.opens - a.opens).slice(0, n);
  }
  return perEditor;
}
